// This is the runtime behaviour common to all quizzes.
export class Quiz extends HTMLElement {
    local_storage_key: string;

    constructor() {
        super();
        this.local_storage_key = `quiz:${window.location.pathname}:${this.dataset.title}`;
    }

    /// Return true if the answer is correct, false if it is incorrect, and null if the answer is
    /// not yet known (e.g. for multi-part quizzes).
    checkAnswer(): Boolean|null {
        // Override in subclasses
        throw new Error('Not implemented');
    }

    reset() {
        // Override in subclasses
        throw new Error('Not implemented');
    }

    /// Set all the answers in the form to the correct values (used when reloading the page and the quiz was already answered)
    setCorrectAnswers() {
        // Override in subclasses
        throw new Error('Not implemented');
    }

    connectedCallback() {
        this.querySelector('form')?.addEventListener('submit', this.handleCheck.bind(this));
        this.querySelector('.reset')?.addEventListener('click', this.handleReset.bind(this));

        const isCompleted = localStorage.getItem(this.local_storage_key) === 'true';
        if (isCompleted) {
            this.setCorrectAnswers();
            this.handleCorrect();
        } else {
            this.handleReset();
        }
    }

    handleCheck(event: Event) {
        event.preventDefault();
        let correct = this.checkAnswer();
        if (correct === null) {
            return;
        }

        if (correct) {
            this.handleCorrect();
        } else {
            this.handleIncorrect();
        }
    }

    handleCorrect() {
        localStorage.setItem(this.local_storage_key, 'true');
        this.classList.remove('unanswered');
        this.classList.remove('incorrect');
        this.classList.add('correct');
        this.dataset.errors = "0";
    }

    handleIncorrect() {
        localStorage.setItem(this.local_storage_key, 'false');
        this.classList.remove('correct');
        this.classList.add('incorrect');
        const errors = parseInt(this.dataset.errors || "0") + 1;
        this.dataset.errors = errors.toString();

        let message = "";
        if (errors <= 5) {
            message = `${"😞".repeat(errors)} Sorry, please try again!`;
        } else if (errors <= 10) {
            message = `${"😒".repeat(errors - 5)} Sorry, please try again!`;
        } else if (errors <= 15) {
            message = `${"😣".repeat(errors - 10)} Sorry, please try again!`;
        } else if (errors <= 20) {
            message = `${"😭".repeat(errors - 15)} Sorry, please try again!`;
        } else {
            message = `💀 x ${errors} Sorry, please try again!`;
        }
        let message_container = this.querySelector('.feedback-when-incorrect p:first-of-type') as HTMLElement;
        message_container.textContent = message;
    }

    handleReset() {
        localStorage.setItem(this.local_storage_key, 'false');
        this.reset();
        this.classList.remove('correct');
        this.classList.remove('incorrect');
        this.classList.add('unanswered');
        this.dataset.errors = "0";
    }

    getAnswer<T>(): Array<T> {
        const data_answer = this.getAttribute('data-answer');
        if (!data_answer) {
            throw new Error(`No answer provided for quiz ${this.getAttribute('data-title')}`);
        }
        const answer = JSON.parse(data_answer) as Array<T>;
        if (!Array.isArray(answer) || answer.length === 0) {
            throw new Error(`Failed to parse answer JSON for quiz ${this.getAttribute('data-title')}`);
        }
        return answer;
    }
}
